import { Hidden, Typography } from '@material-ui/core';
import { Select } from '@room-match/shared-ui-components';
import React, { useCallback, useEffect, useState } from 'react';
import { ISkillCategory } from 'shared/interfaces/ISkills';
import { talentService } from 'shared/services/talentService';
import {
  Container,
  Content,
  CustomTabs,
  HeaderTitle,
  MainCont,
  SkillLabelCont,
  TabHeaderCont,
  TabStyle,
  useStyles,
} from './Skills.styles';
import Accents from './SkillsComponents/Accents/Accents';
import Circus from './SkillsComponents/Circus/Circus';
import Dance from './SkillsComponents/Dance/Dance';
import Instrumental from './SkillsComponents/Instrumental/Instrumental';
import Languages from './SkillsComponents/Languages/Languages';
import PerformanceSkills from './SkillsComponents/PerformanceSkills/PerformanceSkills';
import Sports from './SkillsComponents/Sports/Sports';
import Vocal from './SkillsComponents/Vocal/Vocal';
import YourProfessions from './SkillsComponents/YourProfessions/YourProfessions';

const { getSkills } = talentService();

const Skill = () => {
  const classes = useStyles();
  const { data: skillData, isLoading } = getSkills();
  const [value, setValue] = React.useState<string>('your_professions');
  const [tab, setTab] = React.useState<string>('your_professions');
  const [skills, setSkills] = useState<ISkillCategory[]>([]);

  useEffect(() => {
    setSkills(skillData?.data.attributes.skills || []);
  }, [skillData, isLoading]);

  const getData = useCallback(
    (section: string) => {
      return (skills || []).filter((f) => f?.category === section)[0];
    },
    [skills, isLoading],
  );

  const tabs = [
    {
      name: 'your_professions',
      header: 'Professions',
      component: <YourProfessions />,
      disabled: false,
    },
    {
      name: 'accents',
      header: 'Accents',
      component: <Accents data={getData('Accents')} />,
      disabled: false,
    },
    {
      name: 'languages',
      header: 'Languages',
      component: <Languages data={getData('Languages')} />,
      disabled: false,
    },
    {
      name: 'instrumental',
      header: 'Instrumental',
      component: <Instrumental data={getData('Instrumental')} />,
      disabled: false,
    },
    {
      name: 'dance',
      header: 'Dance',
      component: <Dance data={getData('Dance')} />,
      disabled: false,
    },
    {
      name: 'vocal',
      header: 'Vocal',
      component: <Vocal data={getData('Vocal')} />,
      disabled: false,
    },
    {
      name: 'circus',
      header: 'Circus',
      component: <Circus data={getData('Circus')} />,
      disabled: false,
    },
    {
      name: 'sports',
      header: 'Sports',
      component: <Sports data={getData('Sports')} />,
      disabled: false,
    },
    {
      name: 'performanceSkills',
      header: 'Performance Skills',
      component: (
        <PerformanceSkills
          key={'Performance Skills'}
          category="Performance Skills"
          data={getData('Performance Skills')}
        />
      ),
      disabled: false,
    },
    {
      name: 'willingToConsume',
      header: 'Willing To Consume',
      component: (
        <PerformanceSkills
          key={'Willing To Consume'}
          category="Willing To Consume"
          data={getData('Willing To Consume')}
          skipPopup={true}
          hideMediaViewActions={true}
        />
      ),
      disabled: false,
    },
    {
      name: 'comfortableWith',
      header: 'Comfortable With',
      component: (
        <PerformanceSkills
          key={'Comfortable With'}
          category="Comfortable With"
          data={getData('Comfortable With')}
          skipPopup={true}
          hideMediaViewActions={true}
        />
      ),
      disabled: false,
    },
    {
      name: 'hobies',
      header: 'Hobbies',
      component: <PerformanceSkills key={'Hobbies'} category="Hobbies" data={getData('Hobbies')} />,
      disabled: false,
    },
    {
      name: 'orientation',
      header: 'Orientation',
      component: (
        <PerformanceSkills
          key={'Orientation'}
          category="Orientation"
          data={getData('Orientation')}
          skipPopup={true}
          hideMediaViewActions={true}
        />
      ),
      disabled: false,
    },
  ];

  const getActiveTab = () => {
    return tabs.find((_tab) => _tab.name === tab)?.component;
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTab(newValue);
  };

  const handleDropdownChange = (newValue: string) => {
    setTab(newValue);
  };

  const tabHeader = (title: string) => {
    const headerData = getData(title)?.groups;
    let skillCount = 0;

    if (headerData)
      headerData.map((g) =>
        g.skills.map((s) => {
          if (s.is_selected) skillCount++;
        }),
      );

    return (getData(title)?.professions || []).length ? (
      <TabHeaderCont>
        <HeaderTitle>{title}</HeaderTitle>
        <SkillLabelCont>
          {(getData(title)?.professions || []).length}{' '}
          {(getData(title)?.professions || []).length > 1 ? 'professions' : 'profession'}
        </SkillLabelCont>
      </TabHeaderCont>
    ) : (
      <TabHeaderCont>
        <HeaderTitle>{title}</HeaderTitle>
        {skillCount ? (
          <SkillLabelCont>
            {skillCount} {skillCount > 1 ? 'skills' : 'skill'}
          </SkillLabelCont>
        ) : (
          <></>
        )}
      </TabHeaderCont>
    );
  };

  return (
    <MainCont>
      <Container>
        <Typography style={{ fontSize: 25, marginBottom: 10 }}>Skills</Typography>
        <Hidden smDown>
          <CustomTabs
            orientation="vertical"
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
          >
            {tabs.map((tab, index) => (
              <TabStyle key={index} label={tabHeader(tab.header)} disabled={tab.disabled} value={tab.name} />
            ))}
          </CustomTabs>
        </Hidden>
        <Hidden mdUp>
          <Select
            variant="outlined"
            fullWidth
            data={[
              { value: 'your_professions', key: 'Your Professions' },
              { value: 'accents', key: 'Accents' },
              { value: 'languages', key: 'Languages' },
              { value: 'instrumental', key: 'Instrumental' },
              { value: 'dance', key: 'Dance' },
              { value: 'vocal', key: 'Vocal' },
              { value: 'circus', key: 'Circus' },
              { value: 'sports', key: 'Sports' },
              { value: 'performanceSkills', key: 'Performance Skills' },
            ]}
            placeHolder={'Select'}
            value={tab}
            className={classes.select}
            onChange={(e: any) => handleDropdownChange(e.target.value)}
          />
        </Hidden>
      </Container>
      <Content>{getActiveTab}</Content>
    </MainCont>
  );
};

export default Skill;
